import React, { useEffect, useState } from "react";
import { convertThenFormat } from "../common-data/Converts";
import { useGetFetchNew } from "../../data/Queries";
import { expense_timeline } from "../../data/ApiEndpoints";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";

const ClusterManagerDetails = ({ row, userInfoData }) => {
  const [pageLimit, setPageLimit] = useState(10);
  const [isPage, setIsPage] = useState(0);

  const [filters, setFilters] = useState({
    cm_id: row?.uid,
    page: 1,
    limit: 10,
  });

  const {
    data: expenseTimeline,
    isLoading: expenseTimelineLoading,
    refetch: refetchExpenseTimeline,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "expense_timeline",
    url: expense_timeline,
    filters: filters,
  });

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      cm_id: row?.uid,
      page: isPage + 1,
      limit: pageLimit,
    }));
  }, [row, isPage, pageLimit]);

  useEffect(() => {
    refetchExpenseTimeline();
  }, [filters]);

  const handlePageChange = (newPage) => {
    setIsPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    setIsPage(0); // Reset to first page when changing limit
  };

  const getActionTypeStyle = (actionType) => {
    switch (actionType?.toLowerCase()) {
      case "credit":
        return {
          background: "#e6f4ea",
          color: "#1e7e34",
        };
      case "debit":
        return {
          background: "#fce8e6",
          color: "#d32f2f",
        };
      case "request":
        return {
          background: "#e3f2fd",
          color: "#1976d2",
        };
      case "approval":
        return {
          background: "#e8f5e9",
          color: "#2e7d32",
        };
      case "rejection":
        return {
          background: "#ffebee",
          color: "#c62828",
        };
      default:
        return {
          background: "#f5f5f5",
          color: "#616161",
        };
    }
  };

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return {
          background: "#e6f4ea",
          color: "#1e7e34",
        };
      case "pending":
        return {
          background: "#fff3e0",
          color: "#f57c00",
        };
      case "rejected":
        return {
          background: "#ffebee",
          color: "#c62828",
        };
      default:
        return {
          background: "#f5f5f5",
          color: "#616161",
        };
    }
  };

  const tbColumns = [
    {
      dataField: "timeline_timestamp",
      text: "Timestamp",
      formatter: (_, row) => (
        <span
          className="tb-data minWidth100"
          style={{
            textTransform: "uppercase",
          }}
        >
          {row?.timeline_timestamp || "-"}
        </span>
      ),
    },
    {
      dataField: "expense_category_name",
      text: "Category",
      formatter: (_, row) => (
        <span className="tb-data minWidth100">
          {row?.expense_category_name || "—"}
        </span>
      ),
    },
    {
      dataField: "gym_name",
      text: "Gym",
      formatter: (_, row) => (
        <span className="tb-data minWidth100">{row?.gym_name || "—"}</span>
      ),
    },
    {
      dataField: "performed_by_name",
      text: "Performed By",
      formatter: (_, row) => (
        <span className="tb-data minWidth180">
          <b>{row?.performed_by_name || "NA"}</b>
          {row?.performed_by_account_type && (
            <small className="underline">
              <br />
              {row?.performed_by_account_type}
            </small>
          )}
        </span>
      ),
    },
    {
      dataField: "amount",
      text: "Amount",
      formatter: (_, row) => (
        <span
          className="tb-data minWidth120"
          style={{
            color:
              row?.action_type?.toLowerCase() === "credit"
                ? "#1e7e34"
                : row?.action_type?.toLowerCase() === "debit"
                ? "#d32f2f"
                : "#333",
          }}
        >
          <b>{row?.amount ? `₹${convertThenFormat(row.amount)}` : "—"}</b>
        </span>
      ),
    },
    {
      dataField: "petty_cash_balance",
      text: "Wallet Balance",
      formatter: (_, row) => (
        <span className="tb-data minWidth120" style={{ color: "#2c5aa0" }}>
          <b>
            {row?.petty_cash_balance
              ? `₹${convertThenFormat(row.petty_cash_balance)}`
              : "—"}
          </b>
        </span>
      ),
    },
    {
      dataField: "action_type",
      text: "Action",
      formatter: (_, row) => (
        <span
          style={{
            display: "inline-block",
            padding: "4px 8px",
            borderRadius: "4px",
            ...getActionTypeStyle(row.action_type),
            fontSize: "12px",
            fontWeight: "bold",
            textTransform: "capitalize",
          }}
        >
          {row.action_type}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ marginBottom: "30px" }}>
        <h4
          style={{
            color: "#333",
            marginBottom: "15px",
            borderBottom: "2px solid #e0e0e0",
            paddingBottom: "5px",
          }}
        >
          Cluster Manager Information
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{ fontWeight: "bold", color: "#666", fontSize: "14px" }}
            >
              Name:
            </label>
            <span style={{ color: "#333", fontSize: "16px" }}>
              {row?.name || "NA"}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{ fontWeight: "bold", color: "#666", fontSize: "14px" }}
            >
              Email:
            </label>
            <span style={{ color: "#333", fontSize: "16px" }}>
              {row?.email || "NA"}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{ fontWeight: "bold", color: "#666", fontSize: "14px" }}
            >
              Mobile:
            </label>
            <span style={{ color: "#333", fontSize: "16px" }}>
              {row?.mobile || "NA"}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{ fontWeight: "bold", color: "#666", fontSize: "14px" }}
            >
              Status:
            </label>
            <span
              style={{
                color:
                  row?.status?.toLowerCase() === "active"
                    ? "#28a745"
                    : "#dc3545",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              {row?.status || "NA"}
            </span>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "30px" }}>
        <h4
          style={{
            color: "#333",
            marginBottom: "15px",
            borderBottom: "2px solid #e0e0e0",
            paddingBottom: "5px",
          }}
        >
          Petty Cash Information
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{ fontWeight: "bold", color: "#666", fontSize: "14px" }}
            >
              Wallet Balance:
            </label>
            <span
              style={{ fontWeight: "bold", color: "#2c5aa0", fontSize: "16px" }}
            >
              ₹{convertThenFormat(row?.petty_cash || 0)}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{ fontWeight: "bold", color: "#666", fontSize: "14px" }}
            >
              Total Requested Amount:
            </label>
            <span
              style={{ fontWeight: "bold", color: "#2c5aa0", fontSize: "16px" }}
            >
              ₹{convertThenFormat(row?.total_requested_petty_cash || 0)}
            </span>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "30px" }}>
        <h4
          style={{
            color: "#333",
            marginBottom: "15px",
            borderBottom: "2px solid #e0e0e0",
            paddingBottom: "5px",
          }}
        >
          Mapped Gyms ({row?.cluster_mapped_gyms?.length || 0})
        </h4>
        <div style={{ display: "grid", gap: "10px" }}>
          {row?.cluster_mapped_gyms && row.cluster_mapped_gyms.length > 0 ? (
            row.cluster_mapped_gyms.map((gym, index) => (
              <div
                key={gym.gym_id || index}
                style={{
                  padding: "10px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <div style={{ fontWeight: "bold", color: "#333" }}>
                  {gym.gym_name}
                </div>
                {/* <div
                  style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}
                >
                  ID: {gym.gym_id}
                </div> */}
              </div>
            ))
          ) : (
            <div
              style={{
                color: "#666",
                fontStyle: "italic",
                textAlign: "center",
                padding: "20px",
              }}
            >
              No gyms mapped to this cluster manager
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: "30px" }}>
        <h4
          style={{
            color: "#333",
            marginBottom: "15px",
            borderBottom: "2px solid #e0e0e0",
            paddingBottom: "5px",
          }}
        >
          Transaction History
        </h4>
        {expenseTimelineLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading transactions...
          </div>
        ) : expenseTimeline?.data && expenseTimeline?.data?.length > 0 ? (
          <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table  mt30">
            <PaymentLinkTable
              tableData={expenseTimeline?.data}
              tableColumns={tbColumns}
              isFirstColSticky={true}
              pageLimit={pageLimit}
              setPageLimit={handleLimitChange}
              isPage={isPage}
              setIsPage={handlePageChange}
              tablePagination={expenseTimeline?.pagination}
            />
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "20px",
              color: "#666",
              fontStyle: "italic",
            }}
          >
            No transactions found
          </div>
        )}
      </div>
    </div>
  );
};

export default ClusterManagerDetails;
